import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    Rating,
    Avatar,
    Card,
    CardContent,
    Grid,
    FormControlLabel,
    Switch,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import AdminLayout from '../../../components/AdminLayout';
import axios from 'axios';
import {
    InviteContainer,
    InviteInputRow,
} from './AdminTestimonialsPage.styles';

const backendUrl =
    import.meta.env.MODE === 'development'
        ? `http://${import.meta.env.VITE_BACKEND_URL}`
        : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

const AdminTestimonialsPage = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentTestimonial, setCurrentTestimonial] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        rating: 5,
        content: '',
        date: new Date().toISOString().split('T')[0],
        approved: false,
        avatarUrl: '',
    });
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [filter, setFilter] = useState('all'); // 'all', 'approved', 'pending'
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);

    useEffect(() => {
        const fetchTestimonials = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${backendUrl}/testimonials`);
                setTestimonials(response.data);
            } catch (error) {
                console.error('Error fetching testimonials:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load testimonials. Please try again.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    const handleOpenDialog = (testimonial = null) => {
        if (testimonial) {
            setCurrentTestimonial(testimonial);
            setFormData({
                name: testimonial.name,
                location: testimonial.location,
                rating: testimonial.rating,
                content: testimonial.content,
                date: testimonial.date,
                approved: testimonial.approved,
                avatarUrl: testimonial.avatarUrl,
            });
        } else {
            setCurrentTestimonial(null);
            setFormData({
                name: '',
                location: '',
                rating: 5,
                content: '',
                date: new Date().toISOString().split('T')[0],
                approved: false,
                avatarUrl: '',
            });
        }
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleOpenDeleteDialog = (testimonial) => {
        setCurrentTestimonial(testimonial);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;

        setFormData({
            ...formData,
            [name]:
                name === 'approved'
                    ? checked
                    : type === 'checkbox'
                    ? checked
                    : value,
        });
        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const handleRatingChange = (newValue) => {
        setFormData({
            ...formData,
            rating: newValue,
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.location?.trim()) {
            newErrors.location = 'Location is required';
        }

        if (!formData.content?.trim()) {
            newErrors.content = 'Content is required';
        }

        if (!formData.date) {
            newErrors.date = 'Date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            if (currentTestimonial) {
                // Update existing testimonial
                await axios.put(
                    `${backendUrl}/testimonials/${currentTestimonial._id}`,
                    formData,
                    {
                        headers: {
                            Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                        },
                    }
                );

                // For now, update locally
                const updatedTestimonials = testimonials.map((testimonial) =>
                    testimonial._id === currentTestimonial._id
                        ? { ...testimonial, ...formData }
                        : testimonial
                );
                setTestimonials(updatedTestimonials);

                setSnackbar({
                    open: true,
                    message: 'Testimonial updated successfully!',
                    severity: 'success',
                });
            } else {
                // Create new testimonial
                const response = await axios.post(
                    `${backendUrl}/testimonials`,
                    formData,
                    {
                        headers: {
                            Authorization: token
                                ? `Bearer ${token}`
                                : undefined,
                        },
                    }
                );
                const newTestimonial = response.data;

                setTestimonials([...testimonials, newTestimonial]);

                setSnackbar({
                    open: true,
                    message: 'Testimonial created successfully!',
                    severity: 'success',
                });
            }

            handleCloseDialog();
        } catch (error) {
            console.error('Error saving testimonial:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save testimonial. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentTestimonial) return;

        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            await axios.delete(
                `${backendUrl}/testimonials/${currentTestimonial._id}`,
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                }
            );

            // For now, delete locally
            const filteredTestimonials = testimonials.filter(
                (testimonial) => testimonial._id !== currentTestimonial._id
            );
            setTestimonials(filteredTestimonials);

            setSnackbar({
                open: true,
                message: 'Testimonial deleted successfully!',
                severity: 'success',
            });

            handleCloseDeleteDialog();
        } catch (error) {
            console.error('Error deleting testimonial:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete testimonial. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleApproval = async (testimonial) => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const updatedTestimonial = {
                ...testimonial,
                approved: !testimonial.approved,
            };

            await axios.put(
                `${backendUrl}/testimonials/${testimonial._id}`,
                updatedTestimonial,
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                }
            );

            // For now, update locally
            const updatedTestimonials = testimonials.map((item) =>
                item._id === testimonial._id ? updatedTestimonial : item
            );
            setTestimonials(updatedTestimonials);

            setSnackbar({
                open: true,
                message: `Testimonial ${
                    updatedTestimonial.approved ? 'approved' : 'unapproved'
                } successfully!`,
                severity: 'success',
            });
        } catch (error) {
            console.error('Error updating testimonial approval:', error);
            setSnackbar({
                open: true,
                message:
                    'Failed to update testimonial approval status. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        });
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
    };

    const filteredTestimonials = testimonials.filter((testimonial) => {
        if (filter === 'all') return true;
        if (filter === 'approved') return testimonial.approved;
        if (filter === 'pending') return !testimonial.approved;
        return true;
    });

    const handleSendInvite = async () => {
        setInviteLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await axios.post(
                `${backendUrl}/testimonials/invite`,
                { email: inviteEmail },
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                    },
                }
            );
            setSnackbar({
                open: true,
                message: 'Invite sent successfully!',
                severity: 'success',
            });
            setInviteEmail('');
        } catch (error) {
            console.error('Failed to send invite:', error);
            setSnackbar({
                open: true,
                message: 'Failed to send invite. Please try again.',
                severity: 'error',
            });
        } finally {
            setInviteLoading(false);
        }
    };

    return (
        <AdminLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <InviteContainer>
                    <InviteInputRow>
                        <TextField
                            label="Customer Email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            size="small"
                            disabled={inviteLoading}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendInvite}
                            disabled={inviteLoading || !inviteEmail}
                        >
                            {inviteLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                'Send Invite'
                            )}
                        </Button>
                    </InviteInputRow>
                </InviteContainer>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                    }}
                >
                    <Typography variant="h4" component="h1">
                        Manage Testimonials
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Testimonial
                    </Button>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                    <Button
                        variant={filter === 'all' ? 'contained' : 'outlined'}
                        onClick={() => handleFilterChange('all')}
                    >
                        All
                    </Button>
                    <Button
                        variant={
                            filter === 'approved' ? 'contained' : 'outlined'
                        }
                        color="success"
                        onClick={() => handleFilterChange('approved')}
                    >
                        Approved
                    </Button>
                    <Button
                        variant={
                            filter === 'pending' ? 'contained' : 'outlined'
                        }
                        color="warning"
                        onClick={() => handleFilterChange('pending')}
                    >
                        Pending Approval
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {loading && testimonials.length === 0 ? (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 5,
                            }}
                        >
                            <CircularProgress />
                        </Grid>
                    ) : filteredTestimonials.length === 0 ? (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 5,
                            }}
                        >
                            <Typography variant="body1">
                                No testimonials found.{' '}
                                {filter !== 'all'
                                    ? 'Try changing the filter or add your first testimonial!'
                                    : 'Add your first testimonial!'}
                            </Typography>
                        </Grid>
                    ) : (
                        filteredTestimonials.map((testimonial) => (
                            <Grid item xs={12} md={6} key={testimonial.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        borderLeft: testimonial.approved
                                            ? '4px solid #4caf50'
                                            : '4px solid #ff9800',
                                    }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 16,
                                                right: 16,
                                                display: 'flex',
                                                gap: 1,
                                            }}
                                        >
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() =>
                                                        handleOpenDialog(
                                                            testimonial
                                                        )
                                                    }
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    color="error"
                                                    onClick={() =>
                                                        handleOpenDeleteDialog(
                                                            testimonial
                                                        )
                                                    }
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Avatar
                                                src={testimonial.avatarUrl}
                                                alt={testimonial.name}
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    mr: 2,
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    component="div"
                                                >
                                                    {testimonial.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="primary"
                                                >
                                                    {testimonial.location}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                >
                                                    {new Date(
                                                        testimonial.date +
                                                            'T00:00:00'
                                                    ).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Rating
                                            value={testimonial.rating}
                                            readOnly
                                            precision={0.5}
                                            sx={{ mb: 2 }}
                                        />

                                        <Box sx={{ display: 'flex', mb: 2 }}>
                                            <FormatQuoteIcon
                                                sx={{
                                                    transform: 'rotate(180deg)',
                                                    color: 'primary.main',
                                                    fontSize: 40,
                                                    opacity: 0.5,
                                                    mr: 1,
                                                    alignSelf: 'flex-start',
                                                }}
                                            />
                                            <Typography
                                                variant="body1"
                                                color="primary"
                                                sx={{ fontStyle: 'italic' }}
                                            >
                                                {testimonial.content}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color={
                                                    testimonial.approved
                                                        ? 'success.main'
                                                        : 'warning.main'
                                                }
                                            >
                                                {testimonial.approved
                                                    ? 'Approved'
                                                    : 'Pending Approval'}
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            testimonial.approved
                                                        }
                                                        onChange={() =>
                                                            handleToggleApproval(
                                                                testimonial
                                                            )
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    testimonial.approved
                                                        ? 'Approved'
                                                        : 'Approve'
                                                }
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Add/Edit Testimonial Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {currentTestimonial
                            ? 'Edit Testimonial'
                            : 'Add New Testimonial'}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" noValidate sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        error={!!errors.location}
                                        helperText={errors.location}
                                        required
                                        placeholder="City, State"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography component="legend">
                                            Rating
                                        </Typography>
                                        <Rating
                                            name="rating"
                                            value={formData.rating}
                                            onChange={(event, newValue) => {
                                                handleRatingChange(newValue);
                                            }}
                                            precision={0.5}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Date"
                                        name="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        error={!!errors.date}
                                        helperText={errors.date}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Content"
                                        name="content"
                                        multiline
                                        rows={4}
                                        value={formData.content}
                                        onChange={handleChange}
                                        error={!!errors.content}
                                        helperText={errors.content}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Avatar URL (optional)"
                                        name="avatarUrl"
                                        value={formData.avatarUrl}
                                        onChange={handleChange}
                                        placeholder="Leave blank for random avatar"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ mt: 3 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.approved}
                                                    onChange={handleChange}
                                                    name="approved"
                                                    color="primary"
                                                />
                                            }
                                            label="Approved for display"
                                        />
                                    </Box>
                                </Grid>
                                {formData.avatarUrl && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mt: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ mr: 2 }}
                                            >
                                                Avatar Preview:
                                            </Typography>
                                            <Avatar
                                                src={formData.avatarUrl}
                                                alt="Avatar preview"
                                                sx={{ width: 56, height: 56 }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src =
                                                        'https://i.pravatar.cc/150?img=0';
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the testimonial from
                            "{currentTestimonial?.name}"? This action cannot be
                            undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </AdminLayout>
    );
};

export default AdminTestimonialsPage;
